/* eslint-disable max-len */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { editQuestionnaire } from 'Consts/Questionnaire';
import Currency from 'Components/Format/Currency';
import ShadowBox from 'Components/UI/ShadowBox';
import AccountsHeading from '../Accounts/Heading';
import AccountsTable from '../Accounts/Table';
import AccountsTableInner from '../Accounts/TableInner';
import '../Accounts/index.css';

const RetirementIncome = ({ definedPensionPlans, socialSecurityBenefits, isAgent }) => {
  const simpleHousehold = useSimpleHousehold();
  function getEditLink(page, section) {
    return editQuestionnaire(page, {
      section,
      isAgent,
      householdId: simpleHousehold.householdResource.id(),
    });
  }

  const hasDefinedPensionPlans = definedPensionPlans?.length > 0;
  const hasSocialSecurityBenefits = socialSecurityBenefits?.length > 0;

  return (
    <div className="retirement-income">
      {(hasDefinedPensionPlans || hasSocialSecurityBenefits) && (
        <Fragment>
          <AccountsHeading
            title="Retirement Income"
            editLink={getEditLink('ssiAndPensions', '')}
            editTooltip="Edit Retirement Income"
          >
            <AccountsTable className="accounts-retirement-income">
              <tr className="headings">
                <td className="additions">Additions</td>
                <td className="employer">Employer</td>
                <td className="benefit">Est. Benefit</td>
                <td className="balance">Balance</td>
                <td className="type">Type</td>
                <td className="owner">Owner</td>
                <td className="notes">Notes</td>
              </tr>
            </AccountsTable>
          </AccountsHeading>
          <AccountsTableInner>
            <ShadowBox>
              <AccountsTable className="accounts-retirement-income">
                {/*
                |--------------------------------------------------------------------------
                | Defined Pension Plans

                |--------------------------------------------------------------------------
              */}
                {hasDefinedPensionPlans && (
                  <Fragment>
                    {definedPensionPlans.map(account => (
                      <tr key={`${account.id}dpp`} className="data">
                        <td className="additions">
                          <Currency
                            amount={
                              !Number.isNaN(
                                parseInt(
                                  account.attributes.annualEmployeeContributions,
                                  10,
                                ),
                              )
                                ? parseInt(
                                  account.attributes
                                    .annualEmployeeContributions,
                                  10,
                                )
                                : 0
                            }
                          />
                        </td>
                        <td className="employer">
                          <Currency
                            amount={
                              !Number.isNaN(
                                parseInt(
                                  account.attributes.annualEmployerContributions,
                                  10,
                                ),
                              )
                                ? parseInt(
                                  account.attributes
                                    .annualEmployerContributions,
                                  10,
                                )
                                : 0
                            }
                          />
                        </td>
                        <td className="benefit">
                          {account.attributes.estimatedBenefits}
                        </td>
                        <td className="balance">
                          {account.attributes.balance !== null && (
                            <Currency amount={account.attributes.balance} />
                          )}
                        </td>
                        <td className="type">
                          Pension
                          {account.attributes.financialInstitution !== ''
                          && ` - ${account.attributes.financialInstitution}`}
                        </td>
                        <td className="owner">
                          {account.attributes.accountOwner}
                        </td>
                        <td className="notes">N/A</td>
                      </tr>
                    ))}
                  </Fragment>
                )}
                {/*
                |--------------------------------------------------------------------------
                | Social Security Benefits

                |--------------------------------------------------------------------------
              */}
                {hasSocialSecurityBenefits && (
                  <Fragment>
                    {socialSecurityBenefits.map(account => (
                      <tr key={`${account.id}ssb`} className="data">
                        <td className="additions">N/A</td>
                        <td className="employer">N/A</td>
                        <td className="benefit">
                          {account.attributes.receivingIncome ? (
                            account.attributes.monthlyIncome !== null && (
                              <Currency
                                amount={account.attributes.monthlyIncome}
                              />
                            )
                          ) : (
                            <Fragment>
                              {account.attributes.estimatedBenefitsAge62
                              !== null && (
                                <Fragment>
                                  <div>
                                    <Currency
                                      amount={
                                        account.attributes.estimatedBenefitsAge62
                                      }
                                    />
                                    @62
                                  </div>
                                </Fragment>
                              )}
                              {account.attributes
                                .estimatedBenefitsFullRetirement !== null && (
                                <Fragment>
                                  <div>
                                    <Currency
                                      amount={
                                        account.attributes
                                          .estimatedBenefitsFullRetirement
                                      }
                                    />
                                    @FRA
                                  </div>
                                </Fragment>
                              )}
                              {account.attributes.estimatedBenefitsAge70
                              !== null && (
                                <Fragment>
                                  <div>
                                    <Currency
                                      amount={
                                        account.attributes.estimatedBenefitsAge70
                                      }
                                    />
                                    @70
                                  </div>
                                </Fragment>
                              )}
                            </Fragment>
                          )}
                        </td>
                        <td className="balance">N/A</td>
                        <td className="type">
                          Social Security
                          {account.attributes.type
                          && ` - ${account.attributes.type}`}
                        </td>
                        <td className="owner">
                          {account.attributes.benefitRecipient}
                        </td>
                        <td className="notes">
                          {account.attributes.receivingIncome
                            ? 'Currently Receiving'
                            : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                )}
              </AccountsTable>
            </ShadowBox>
          </AccountsTableInner>
        </Fragment>
      )}
    </div>
  );
};

RetirementIncome.propTypes = {
  definedPensionPlans: PropTypes.array.isRequired,
  socialSecurityBenefits: PropTypes.array.isRequired,
  isAgent: PropTypes.bool.isRequired,
};

export default RetirementIncome;
